import { useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import type {
  EnvironmentKey,
  DomainEntity,
  ConfigEntity,
  EntityRootData,
} from '@web-config-app/core';
import { API_JSON_CONTENT_TYPE } from '@web-config-app/api';
import {
  UseEntityCreateOptions,
  useEntityCreate,
} from '../use-entity-create/use-entity-create';
import { alterDataForCloneEntity } from '../../utilities/alter-data-for-clone-entity/alter-data-for-clone-entity';

export type UseEntityCloneResult = ReturnType<typeof useEntityClone>;
export interface UseEntityCloneProps {
  entity: ConfigEntity | DomainEntity;
  data?: EntityRootData;
  headers?: HeadersInit;
  environment: EnvironmentKey;
  options?: UseEntityCreateOptions;
  isUsingConfigAppBackend?: boolean;
}

/**
 * Provides functionality for shallow cloning an entity.
 *
 * This hook utilizes the `useEntityCreate` hook to create a new draft entity based on the provided data.
 * It modifies the data by removing the `id` property and prefixing the entity name with a defined prefix.
 *
 * @param {Entity} params.entity - The entity configuration.
 * @param {EntityRootData} params.data - The entity data to be cloned.
 * @param {UseMutationOptions} [params.options] - Options for the `useEntityCreate` mutation.
 *
 * @returns {Object} An object containing the `cloneEntity` object with an additional `enableAction` property.
 * The `enableAction` property indicates whether the clone action is enabled based on the entity configuration.
 */
export const useEntityClone = ({
  entity,
  data,
  options,
  environment,
  isUsingConfigAppBackend,
}: UseEntityCloneProps) => {
  const { formatMessage } = useIntl();
  const { endpoints } = entity;
  const entityNamePath = isUsingConfigAppBackend
    ? '$.attributes.entityMetadata.name'
    : entity.schema?.['x-entity-metadata']?.entityNamePath;

  const prefix = formatMessage({ id: 'DUPLICATE_OF' });

  const clonedData = useMemo(
    () =>
      data &&
      alterDataForCloneEntity({
        data,
        schema: entity.schema,
        entityNamePath,
        prefix,
      }),
    [data, entity.schema, entityNamePath, prefix],
  );

  const cloneEntity = useEntityCreate({
    entity,
    environment,
    data: clonedData,
    options,
    headers: {
      'Content-Type': API_JSON_CONTENT_TYPE,
    },
    operation: 'clone',
  });

  const clone = {
    ...cloneEntity,
    enableAction:
      // `clone` is dependent on the `create` operation
      !!endpoints.clone && !!endpoints.create,
  };
  return clone;
};
